import type { ExperimentConfig, ManualEvent, RunStats, Sample, StopReason } from '../simulation/types';

/** Stored trace fields (columnar, rounded) - bounded and decimated, never unbounded frame data. */
export const STORED_FIELDS = [
  't', 'z', 'v', 'a', 'r', 'ym', 'e', 'Tff', 'uP', 'uI', 'uD', 'ufb', 'Tcmd', 'T', 'd', 'I', 'vEst', 'sat', 'sw',
] as const satisfies readonly (keyof Sample)[];

export type CompactTrace = Record<(typeof STORED_FIELDS)[number], number[]>;

/** Store every Nth recorded sample (recorded every 0.025 s → stored every 0.05 s, so lesson question times fall on stored samples). */
export const STORE_STRIDE = 2;

export interface RunRecord {
  id: string;
  /** Sequential display number, e.g. "Run 7". */
  number: number;
  createdAt: number;
  presetId: string;
  presetVersion: number;
  /** Lab step this run was recorded under (null for free exploration). */
  labStepId: string | null;
  config: ExperimentConfig;
  manualEvents: ManualEvent[];
  status: 'complete' | 'stopped';
  stopReason: StopReason | null;
  stats: RunStats;
  trace: CompactTrace;
  engine: string;
}

const round = (x: number) => (Number.isFinite(x) ? Math.round(x * 1e5) / 1e5 : 0);

export function compactTrace(trace: Sample[], stride = STORE_STRIDE): CompactTrace {
  const out = Object.fromEntries(STORED_FIELDS.map((f) => [f, [] as number[]])) as CompactTrace;
  const push = (s: Sample) => {
    for (const f of STORED_FIELDS) out[f].push(round(s[f] as number));
  };
  trace.forEach((s, i) => {
    if (i % stride === 0 || i === trace.length - 1) push(s);
  });
  return out;
}

export function expandTrace(c: CompactTrace): Sample[] {
  const n = c.t.length;
  const out: Sample[] = new Array(n);
  for (let i = 0; i < n; i++) {
    const s = {} as Record<string, number>;
    for (const f of STORED_FIELDS) s[f] = c[f][i];
    out[i] = s as unknown as Sample;
  }
  return out;
}

/** Stored sample nearest to time t. */
export function sampleNear(c: CompactTrace, t: number): Sample {
  let best = 0;
  for (let i = 1; i < c.t.length; i++) if (Math.abs(c.t[i] - t) < Math.abs(c.t[best] - t)) best = i;
  const s = {} as Record<string, number>;
  for (const f of STORED_FIELDS) s[f] = c[f][best];
  return s as unknown as Sample;
}

/** Leaf paths where two JSON-like values differ (arrays compared as whole values). */
export function diffPaths(a: unknown, b: unknown, prefix = ''): string[] {
  if (Array.isArray(a) || Array.isArray(b)) {
    return JSON.stringify(a) === JSON.stringify(b) ? [] : [prefix];
  }
  if (a && b && typeof a === 'object' && typeof b === 'object') {
    const keys = new Set([...Object.keys(a as object), ...Object.keys(b as object)]);
    const out: string[] = [];
    for (const k of keys) {
      out.push(...diffPaths((a as Record<string, unknown>)[k], (b as Record<string, unknown>)[k], prefix ? `${prefix}.${k}` : k));
    }
    return out;
  }
  if (typeof a === 'number' && typeof b === 'number' && Math.abs(a - b) < 1e-12) return [];
  return a === b ? [] : [prefix];
}

/** Human description of a config path, for fairness warnings and lab messages. */
export const PATH_LABELS: Record<string, string> = {
  'plant.m': 'mass m',
  'plant.g': 'gravity g',
  'plant.k': 'spring stiffness k',
  'plant.b': 'damping b',
  'plant.zs': 'spring rest altitude z_s',
  'plant.Tmax': 'maximum thrust T_max',
  z0: 'operating point z_0',
  'initial.z': 'initial altitude',
  'initial.v': 'initial velocity',
  'reference.initial': 'initial target',
  'reference.steps': 'target changes',
  'disturbance.initial': 'initial disturbance',
  'disturbance.steps': 'disturbance schedule',
  'noise.enabled': 'sensor noise on/off',
  'noise.sigma': 'noise level',
  'noise.seed': 'noise seed',
  duration: 'run duration',
  dt: 'step size Δt',
  'controller.kind': 'controller type',
  'controller.ffMode': 'feedforward mode',
  'controller.estimated': 'estimated parameters',
  'controller.Kp': 'K_p',
  'controller.Ki': 'K_i',
  'controller.Kd': 'K_d',
  'controller.antiWindup': 'anti-windup',
  'controller.derivTau': 'derivative filter τ',
  'controller.bangDelta': 'bang-bang amplitude',
  'controller.hysteresis': 'hysteresis h',
  'controller.bangInitial': 'initial switch state',
  'controller.manualInput': 'manual input',
};

export const labelPath = (p: string) => PATH_LABELS[p] ?? p;

/** Paths that make a comparison between runs unfair (the test itself differs, not the tuning). */
export function scenarioDifferences(a: ExperimentConfig, b: ExperimentConfig): string[] {
  const scenarioA = { plant: a.plant, z0: a.z0, initial: a.initial, reference: a.reference, disturbance: a.disturbance, noise: a.noise, duration: a.duration, dt: a.dt };
  const scenarioB = { plant: b.plant, z0: b.z0, initial: b.initial, reference: b.reference, disturbance: b.disturbance, noise: b.noise, duration: b.duration, dt: b.dt };
  return diffPaths(scenarioA, scenarioB).filter((p) => !(p === 'noise.seed' || p === 'noise.sigma') || a.noise.enabled || b.noise.enabled);
}

export function describeController(c: ExperimentConfig['controller']): string {
  const g = (x: number) => +x.toFixed(3);
  switch (c.kind) {
    case 'manual':
      return c.ffMode === 'off' ? 'Manual thrust' : 'Feedforward + manual';
    case 'open-loop':
      return c.ffMode === 'off' ? 'Open loop (no thrust)' : 'Open loop (feedforward only)';
    case 'bang-bang':
      return `Bang-bang ±${g(c.bangDelta)} N, h=${g(c.hysteresis)} m`;
    case 'p':
      return `P  Kp=${g(c.Kp)}`;
    case 'pi':
      return `PI  Kp=${g(c.Kp)} Ki=${g(c.Ki)}${c.antiWindup ? '' : ' (no anti-windup)'}`;
    case 'pid':
      return `PID  Kp=${g(c.Kp)} Ki=${g(c.Ki)} Kd=${g(c.Kd)}${c.derivTau ? ` τ=${g(c.derivTau)}` : ''}`;
  }
}

export const FF_LABELS: Record<ExperimentConfig['controller']['ffMode'], string> = {
  off: 'Feedforward off',
  'operating-point': 'Feedforward T₀',
  'target-equilibrium': 'Feedforward: target equilibrium',
};
